import { createReducer, on } from '@ngrx/store';
import { ServiceRequestDto } from '../../../../api/gse/models/service-request-dto';
import { EquipmentDto } from '../../../../api/gse/models/equipment-dto';
import { ChecklistDto } from '../../../../api/gse/models/checklist-dto';
import { JobCardDto } from '../../../../api/gse/models/job-card-dto';
import { ChecklistAnswerValueDto } from '../../../../api/gse/models/checklist-answer-value-dto';
import { ChecklistAnswerDto } from '../../../../api/gse/models/checklist-answer-dto';
import { JobServiceWithLogsDto } from '../../../../api/gse/models/job-service-with-logs-dto';
import { JobCardStatus } from '../../../../api/gse/models/job-card-status';
import { GetAllJobCards$Params } from '../../../../api/gse/fn/job-card/get-all-job-cards';
import { GetAllServiceRequests$Params } from '../../../../api/gse/fn/service-requests/get-all-service-requests';
import * as ServiceRequestActions from './service-request.actions';

/** Overlay description onto list after check-in update when API list lags persistence */
type CheckInDescriptionPatch = { serviceRequestId: number; description: string };

function mergeServiceRequestFromResponse(
  requests: ServiceRequestDto[],
  response?: { data?: Array<ServiceRequestDto | undefined> | null }
): ServiceRequestDto[] {
  const updated = response?.data?.[0];
  if (updated?.id == null) {
    return requests;
  }
  return requests.map((sr) => (sr.id === updated.id ? { ...sr, ...updated } : sr));
}

function applyCheckInDescriptionPatch(
  requests: ServiceRequestDto[],
  patch: CheckInDescriptionPatch | null | undefined
): ServiceRequestDto[] {
  if (!patch) {
    return requests;
  }
  return requests.map((sr) =>
    sr.id === patch.serviceRequestId ? { ...sr, description: patch.description } : sr
  );
}

export interface ServiceRequestState {
  serviceRequests: ServiceRequestDto[];
  serviceRequestTotalRecords: number;
  loading: boolean;
  error: string | null;
  selectedId: number | null;
  /** Applied on next loadServiceRequestsSuccess when that row is in the page */
  lastCheckInDescriptionPatch: CheckInDescriptionPatch | null;

  jobCards: JobCardDto[];
  jobCardTotalRecords: number;
  jobCardLoading: boolean;
  selectedJobCardId: number | null;

  equipmentOptions: EquipmentDto[];
  equipmentLoading: boolean;
  equipmentPage: number;
  equipmentTotalElements: number;
  equipmentHasMore: boolean;

  checklists: ChecklistDto[];
  checklistsLoading: boolean;

  submitting: boolean;
  submitSuccess: boolean;

  jobCardChecklistAnswers: ChecklistAnswerValueDto[];
  jobCardChecklistAnswerRecords: ChecklistAnswerDto[];
  jobCardChecklistAnswersLoading: boolean;

  jobCardWorkLogs: JobServiceWithLogsDto[];
  jobCardWorkLogsLoading: boolean;

  jobCardChecklistQuestions: ChecklistDto[];
  jobCardChecklistQuestionsLoading: boolean;

  lastJobCardParams: GetAllJobCards$Params | null;
  lastServiceRequestParams: GetAllServiceRequests$Params | null;
  recreateSubmitting: boolean;
}

export const initialState: ServiceRequestState = {
  serviceRequests: [],
  serviceRequestTotalRecords: 0,
  lastServiceRequestParams: null,
  loading: false,
  error: null,
  selectedId: null,
  lastCheckInDescriptionPatch: null,

  jobCards: [],
  jobCardTotalRecords: 0,
  jobCardLoading: false,
  selectedJobCardId: null,

  equipmentOptions: [],
  equipmentLoading: false,
  equipmentPage: 0,
  equipmentTotalElements: 0,
  equipmentHasMore: false,

  checklists: [],
  checklistsLoading: false,

  submitting: false,
  submitSuccess: false,

  jobCardChecklistAnswers: [],
  jobCardChecklistAnswerRecords: [],
  jobCardChecklistAnswersLoading: false,
  jobCardWorkLogs: [],
  jobCardWorkLogsLoading: false,

  jobCardChecklistQuestions: [],
  jobCardChecklistQuestionsLoading: false,

  lastJobCardParams: null,
  recreateSubmitting: false,
};

export const serviceRequestReducer = createReducer(
  initialState,
  on(ServiceRequestActions.loadServiceRequests, (state, { params, silent }) => ({
    ...state,
    loading: silent ? state.loading : true,
    lastServiceRequestParams: params,
    error: null,
  })),
  on(
    ServiceRequestActions.loadServiceRequestsSuccess,
    (state, { data, totalRecords, preserveSelection }) => {
      let serviceRequests = data;
      const patch = state.lastCheckInDescriptionPatch;
      if (patch && data.some((d) => d.id === patch.serviceRequestId)) {
        serviceRequests = applyCheckInDescriptionPatch(data, patch);
      }
      return {
        ...state,
        serviceRequests,
        serviceRequestTotalRecords: totalRecords,
        loading: false,
        lastCheckInDescriptionPatch: null,
        selectedId: preserveSelection
          ? state.selectedId && data.some((d) => d.id === state.selectedId)
            ? state.selectedId
            : data[0]?.id || null
          : data[0]?.id || null,
      };
    }
  ),
  on(ServiceRequestActions.loadServiceRequestsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error,
  })),
  on(ServiceRequestActions.selectServiceRequest, (state, { id }) => ({
    ...state,
    selectedId: id,
  })),

  // Equipment Options
  // Do not clear equipmentOptions here: non-append searches would show an empty list
  // until the HTTP response arrives. Success replaces options; open dialog uses clearEquipmentOptions first.
  on(ServiceRequestActions.loadEquipmentOptions, (state) => ({
    ...state,
    equipmentLoading: true,
    error: null,
  })),
  on(
    ServiceRequestActions.loadEquipmentOptionsSuccess,
    (state, { data, totalElements, page, last, append }) => {
      const newOptions = append
        ? [...state.equipmentOptions, ...data.filter((d) => !state.equipmentOptions.some((eo) => eo.id === d.id))]
        : data;
      return {
        ...state,
        equipmentOptions: newOptions,
        equipmentLoading: false,
        equipmentPage: page,
        equipmentTotalElements: totalElements,
        equipmentHasMore: !last,
      };
    }
  ),
  on(ServiceRequestActions.loadEquipmentOptionsFailure, (state, { error }) => ({
    ...state,
    equipmentLoading: false,
    error,
  })),
  on(ServiceRequestActions.clearEquipmentOptions, (state) => ({
    ...state,
    equipmentOptions: [],
    equipmentPage: 0,
    equipmentHasMore: false,
    equipmentTotalElements: 0,
  })),

  // Checklists
  on(ServiceRequestActions.loadChecklists, (state) => ({
    ...state,
    checklistsLoading: true,
    error: null,
  })),
  on(ServiceRequestActions.loadChecklistsSuccess, (state, { checklists }) => ({
    ...state,
    checklists: checklists,
    checklistsLoading: false,
  })),
  on(ServiceRequestActions.loadChecklistsFailure, (state, { error }) => ({
    ...state,
    checklistsLoading: false,
    error,
  })),

  // Submissions
  on(
    ServiceRequestActions.submitCheckIn,
    ServiceRequestActions.updateCheckIn,
    (state) => ({
      ...state,
      submitting: true,
      submitSuccess: false,
      error: null,
      lastCheckInDescriptionPatch: null,
    })
  ),
  on(
    ServiceRequestActions.createServiceRequest,
    ServiceRequestActions.createJobCard,
    ServiceRequestActions.updateJobCard,
    ServiceRequestActions.startJobServiceWorkLog,
    ServiceRequestActions.stopJobServiceWorkLog,
    ServiceRequestActions.finishJobServiceWorkLog,
    (state) => ({
      ...state,
      submitting: true,
      submitSuccess: false,
      error: null,
    })
  ),
  on(
    ServiceRequestActions.createServiceRequestSuccess,
    (state, { response }) => ({
      ...state,
      submitting: false,
      submitSuccess: true,
      selectedId: response?.data?.[0]?.id ?? state.selectedId,
    })
  ),
  on(
    ServiceRequestActions.createJobCardSuccess,
    (state) => ({
      ...state,
      submitting: false,
      submitSuccess: true,
    })
  ),
  on(
    ServiceRequestActions.submitCheckInSuccess,
    ServiceRequestActions.updateCheckInSuccess,
    (state, { response, clientDescriptionPatch }) => {
      let serviceRequests = mergeServiceRequestFromResponse(state.serviceRequests, response);
      serviceRequests = applyCheckInDescriptionPatch(serviceRequests, clientDescriptionPatch);
      const newId = response?.data?.[0]?.id ?? null;
      return {
        ...state,
        serviceRequests,
        lastCheckInDescriptionPatch: clientDescriptionPatch ?? null,
        submitting: false,
        submitSuccess: true,
        // Select the newly created/updated check-in immediately
        selectedId: newId ?? state.selectedId,
      };
    }
  ),
  on(
    ServiceRequestActions.updateJobCardSuccess,
    (state, { response }) => {
      const updatedJobCard = response.data?.[0];
      let newJobCards = state.jobCards;
      if (updatedJobCard && updatedJobCard.id) {
        newJobCards = state.jobCards.map((jc) =>
          jc.id === updatedJobCard.id ? { ...jc, ...updatedJobCard } : jc
        );
      }
      return {
        ...state,
        jobCards: newJobCards,
        submitting: false,
        submitSuccess: true,
      };
    }
  ),
  on(
    ServiceRequestActions.createServiceRequestFailure,
    ServiceRequestActions.submitCheckInFailure,
    ServiceRequestActions.updateCheckInFailure,
    ServiceRequestActions.createJobCardFailure,
    ServiceRequestActions.updateJobCardFailure,
    (state, { error }) => ({
      ...state,
      submitting: false,
      submitSuccess: false,
      error,
    })
  ),

  // Job Cards
  on(ServiceRequestActions.loadJobCards, (state, { params, silent }) => ({
    ...state,
    jobCardLoading: silent ? state.jobCardLoading : true,
    lastJobCardParams: params,
    error: null,
  })),
  on(
    ServiceRequestActions.loadJobCardsSuccess,
    (state, { data, totalRecords, preserveSelection }) => ({
      ...state,
      jobCards: data,
      jobCardTotalRecords: totalRecords,
      jobCardLoading: false,
      selectedJobCardId: preserveSelection
        ? state.selectedJobCardId && data.some((d) => d.id === state.selectedJobCardId)
          ? state.selectedJobCardId
          : data[0]?.id || null
        : data[0]?.id || null,
    })
  ),
  on(ServiceRequestActions.loadJobCardsFailure, (state, { error }) => ({
    ...state,
    jobCardLoading: false,
    error,
  })),
  on(ServiceRequestActions.selectJobCard, (state, { id }) => ({
    ...state,
    selectedJobCardId: id,
    jobCardChecklistAnswers: [],
    jobCardWorkLogs: [],
    jobCardChecklistQuestions: [],
  })),

  on(ServiceRequestActions.loadJobCardChecklistQuestions, (state) => ({
    ...state,
    jobCardChecklistQuestionsLoading: true,
    jobCardChecklistQuestions: [],
    error: null,
  })),
  on(ServiceRequestActions.loadJobCardChecklistQuestionsSuccess, (state, { checklists }) => ({
    ...state,
    jobCardChecklistQuestions: checklists ?? [],
    jobCardChecklistQuestionsLoading: false,
  })),
  on(ServiceRequestActions.loadJobCardChecklistQuestionsFailure, (state, { error }) => ({
    ...state,
    jobCardChecklistQuestionsLoading: false,
    jobCardChecklistQuestions: [],
    error,
  })),

  // Job Card Checklist Answers
  on(ServiceRequestActions.loadJobCardChecklistAnswers, (state, { silent }) => ({
    ...state,
    jobCardChecklistAnswersLoading: !silent,
    error: null,
  })),
  on(ServiceRequestActions.loadJobCardChecklistAnswersSuccess, (state, { records, answers }) => ({
    ...state,
    jobCardChecklistAnswers: answers,
    jobCardChecklistAnswerRecords: records,
    jobCardChecklistAnswersLoading: false,
  })),
  on(ServiceRequestActions.loadJobCardChecklistAnswersFailure, (state, { error }) => ({
    ...state,
    jobCardChecklistAnswersLoading: false,
    error,
  })),

  // Job Card status update – keep local jobCards array in sync so UI updates without full reload
  on(ServiceRequestActions.updateJobCardStatusSuccess, (state, { params }) => {
    const id = params.id;
    const newStatus = params.body?.status as JobCardStatus | undefined;
    if (id == null || !newStatus) {
      return state;
    }
    const reasonFromRequest = params.body?.reason;
    const updatedJobCards = state.jobCards.map((jc) => {
      if (jc.id !== id) {
        return jc;
      }
      const next = { ...jc, status: newStatus };
      if (reasonFromRequest !== undefined) {
        next.reason = reasonFromRequest;
      }
      return next;
    });
    return {
      ...state,
      jobCards: updatedJobCards,
    };
  }),

  // Job Card work logs
  on(ServiceRequestActions.loadJobCardWorkLogs, (state) => ({
    ...state,
    jobCardWorkLogsLoading: true,
    error: null,
  })),
  on(ServiceRequestActions.loadJobCardWorkLogsSuccess, (state, { logs }) => ({
    ...state,
    jobCardWorkLogs: logs,
    jobCardWorkLogsLoading: false,
  })),
  on(ServiceRequestActions.loadJobCardWorkLogsFailure, (state, { error }) => ({
    ...state,
    jobCardWorkLogsLoading: false,
    error,
  })),

  // Recreate Job Card
  on(ServiceRequestActions.recreateJobCard, (state) => ({
    ...state,
    recreateSubmitting: true,
    error: null,
  })),
  on(ServiceRequestActions.recreateJobCardSuccess, (state) => ({
    ...state,
    recreateSubmitting: false,
  })),
  on(ServiceRequestActions.recreateJobCardFailure, (state, { error }) => ({
    ...state,
    recreateSubmitting: false,
    error,
  }))
);
